import {
	AEM_DICTIONARY_COLLECTIONS,
	useAEMDictionary
} from "@AEM/hooks/useAEMDictionary"
import { showErrorNotification } from "@components/ui/Notification/Notification"
import { useAuthContext } from "@context/AuthProvider"
import { useOTPMutations } from "@hooks/useOTPMutations"
import { Button, Card, Group, Stack, Text } from "@mantine/core"
import { sendEmailOTP } from "@pages/profile-managment/services/agentService"
import { MFA_METHODS } from "@utils/constants.util"
import { isAxiosError } from "axios"
import { useSecurityMutations } from "../hooks/useSecurityMutations"
import { useSecuritySectionStore } from "../stores/securitySectionStore"
import { GenericVerificationFlow } from "./GenericVerificationFlow"

export const UsernameToEmail = () => {
	const { user, goToIdle, updateUser, setToken, token } =
		useSecuritySectionStore()
	const { validateOTP, isLoading } = useOTPMutations()
	const { updateSecurity } = useSecurityMutations()
	const { dictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)
	const { refreshUserProfile } = useAuthContext()

	const handleCancel = () => {
		goToIdle()
	}

	const handleConfirmConversion = async () => {
		if (!user?.email || !user?.emailVerified) return
		try {
			await updateSecurity.mutateAsync({
				setEmailAsUsername: true
			})
			const updatedProfile = await refreshUserProfile()
			updateUser({ emailIsUsername: updatedProfile.emailIsUsername })
			goToIdle()
		} catch {}
	}

	const handleSend = async (email: string) => {
		const errorMessage = dictionary?.emailAlreadyRegistered
		const result = await sendEmailOTP(email, errorMessage)
		setToken(result.token)
		return result.success
	}

	const handleVerify = async (otp: string) => {
		try {
			const otpValidation = await validateOTP.mutateAsync({
				token: token ?? "",
				otp
			})
			if (otpValidation?.data?.success) {
				await handleSuccess(otpValidation?.data?.token ?? "")
				return true
			}
		} catch {}
		return false
	}

	const handleSuccess = async (token: string) => {
		try {
			await updateSecurity.mutateAsync({
				token: token ?? "",
				setEmailAsUsername: true,
				setEmailValidated: true,
				primaryMFA: MFA_METHODS.EMAIL
			})
			updateUser({ emailVerified: true, primaryMFA: MFA_METHODS.EMAIL })
		} catch (error) {
			const isEmailAlreadyExists =
				isAxiosError(error) &&
				error.response?.data?.message?.errorCode === "EMAIL_ALREADY_EXISTS"
			showErrorNotification(
				isEmailAlreadyExists
					? dictionary?.emailAlreadyRegistered ||
							"Failed to update security settings"
					: "Failed to update security settings"
			)
		}
	}

	if (user?.emailVerified) {
		return (
			<Stack gap={40} maw={920}>
				<Card p={40} radius="sm" shadow="sm" bg="var(--background-color-card)">
					<Group justify="space-between" align="center">
						<Text size="xl" fw={600}>
							{dictionary?.makeYourEmailYourUsernameLabel}
						</Text>
						<Button
							variant="link"
							size="sm"
							onClick={handleCancel}
							pt={4}
							pb={4}
							h="auto"
							mih={26}
							lh={1.4}
							w={{ base: "100%", sm: "auto" }}
							maw={{ base: "100%", sm: "none" }}
							radius={0}
							styles={{
								label: {
									whiteSpace: "normal",
									wordBreak: "break-word",
									overflowWrap: "break-word"
								}
							}}
						>
							{dictionary?.cancelButtonLabel}
						</Button>
					</Group>

					<Stack gap="lg" align="flex-start" mt="xs">
						<Text size="sm">
							{dictionary?.makeYourEmailYourUsernameDescription}
						</Text>

						<Stack gap="xs">
							<Text fw={600}>{dictionary?.verifiedEmailAddressLabel}</Text>
							<Text
								size="xl"
								style={{
									wordBreak: "break-word",
									overflowWrap: "break-word"
								}}
							>
								{user?.email}
							</Text>
						</Stack>

						<Button
							variant="dark-blue"
							size="md"
							mt="md"
							loading={updateSecurity.isPending}
							onClick={handleConfirmConversion}
							w={{ base: "100%", sm: "auto" }}
							maw={{ base: "100%", sm: "none" }}
							styles={{
								label: {
									whiteSpace: "normal",
									wordBreak: "break-word",
									overflowWrap: "break-word"
								}
							}}
						>
							{dictionary?.confirmChangeButtonLabel}
						</Button>
					</Stack>
				</Card>
			</Stack>
		)
	}

	return (
		<GenericVerificationFlow
			title={dictionary?.makeYourEmailYourUsernameLabel || ""}
			description={dictionary?.unVerfiedEmailAdress || ""}
			inputLabel={dictionary?.yourEmailAddressLabel || ""}
			inputPlaceholder=""
			inputType="email"
			inputDisabled
			initialValue={user?.email || ""}
			validateInput={() => null}
			onSend={handleSend}
			onVerify={handleVerify}
			resendFunction={handleSend}
			isLoading={isLoading}
			goBack={handleCancel}
		/>
	)
}
